import type Phaser from "phaser";
import type { NpcLook } from "odyssey-shared";
import type { DialoguePortrait } from "../ui/kit/DialoguePanel.tsx";
import { CharacterSprite, type CharacterFigure, type CharacterManifest } from "./character.ts";
import type { CreatureHeads } from "./creature-heads.ts";
import { HEAD_ABOVE_FEET } from "./world-ui/characters.ts";

/**
 * How the game page draws and names an NPC (ADR-136 decisions 1 and 2.4) from the version's NPC look table and the
 * character manifest: its figure at its look's scale, how far its art reaches above its feet at that scale, its name
 * and the portrait the dialogue window shows. An NPC is never drawn from the composed crowd: its look is its own sheet.
 */

/**
 * An NPC's name.
 *
 * @param looks The version's NPC look table.
 * @param appearance The NPC's appearance, its index in the table.
 * @returns The name, or "" past the table's end.
 */
export function npcName(looks: readonly NpcLook[], appearance: number): string {
  return looks[appearance]?.name ?? "";
}

/**
 * An NPC's portrait for the dialogue window: its sheet's portrait strip with the clip table that animates it
 * (P2.22-T37), else the first frame of its idle strip facing down, which the window crops from the strip.
 *
 * @param manifest The character manifest.
 * @param looks The version's NPC look table.
 * @param appearance The NPC's appearance.
 * @param base URL prefix the manifest's paths sit under.
 * @returns The strip's URL and its frame size in pixels, or null past the table's end or for a sheet with no idle strip.
 */
export function npcPortrait(manifest: CharacterManifest, looks: readonly NpcLook[], appearance: number, base: string): DialoguePortrait | null {
  const sheet = manifest.characters[looks[appearance]?.sheet ?? ""];
  const clips = sheet?.portrait;
  if (clips?.url !== undefined) {
    return { url: `${base}/${clips.url}`, frameWidth: clips.frameWidth, frameHeight: clips.frameHeight, clips };
  }
  const file = sheet?.anims["idle"]?.files.down;
  if (sheet === undefined || file === undefined) {
    return null;
  }
  return { url: `${base}/${file}`, frameWidth: sheet.frameWidth, frameHeight: sheet.frameHeight };
}

/**
 * Creates an NPC's figure with the sheet its look names, drawn at its look's scale about its feet.
 *
 * @param scene The scene.
 * @param manifest The character manifest.
 * @param looks The version's NPC look table.
 * @param appearance The NPC's appearance.
 * @param depthOf Depth for a feet position in pixels, as a character sorts.
 * @returns The figure, or null when the table has no such look or the manifest no such sheet.
 */
export function npcFigure(scene: Phaser.Scene, manifest: CharacterManifest, looks: readonly NpcLook[], appearance: number, depthOf: (feetX: number, feetY: number) => number): CharacterFigure | null {
  const look = looks[appearance];
  if (look === undefined || manifest.characters[look.sheet] === undefined) {
    return null;
  }
  return new CharacterSprite(scene, manifest, [look.sheet], depthOf).setDrawScale(look.scale ?? 1);
}

/**
 * How far an NPC's art reaches above its feet, measured once per sheet as a creature's is, times its look's scale, so
 * its plate and the prompt's bracket follow the size it is drawn at.
 *
 * @param heads The page's per-sheet head measures.
 * @param manifest The character manifest.
 * @param looks The version's NPC look table.
 * @param appearance The NPC's appearance.
 * @returns The height in world pixels, `HEAD_ABOVE_FEET` past the table's end.
 */
export function npcHead(heads: CreatureHeads, manifest: CharacterManifest, looks: readonly NpcLook[], appearance: number): number {
  const look = looks[appearance];
  return look === undefined ? HEAD_ABOVE_FEET : heads.of(manifest, look.sheet) * (look.scale ?? 1);
}
